import { useEffect, useState } from 'react';
import { supabase } from '../supabase';

export interface TeamMetrics {
  sessionsThisWeek: number;
  drillMinutesPlanned: number;
  squadAvailable: number;
  squadTotal: number;
  squadLimited: number;
}

const ZERO: TeamMetrics = {
  sessionsThisWeek: 0,
  drillMinutesPlanned: 0,
  squadAvailable: 0,
  squadTotal: 0,
  squadLimited: 0,
};

interface SessionDrillDurationRow {
  drills?: { duration_mins?: number | null } | Array<{ duration_mins?: number | null }> | null;
}

function durationFromSessionDrill(row: SessionDrillDurationRow): number {
  const drill = Array.isArray(row.drills) ? row.drills[0] : row.drills;
  return typeof drill?.duration_mins === 'number' ? drill.duration_mins : 0;
}

export function useTeamMetrics(teamId: string | undefined) {
  const [metrics, setMetrics] = useState<TeamMetrics>(ZERO);

  useEffect(() => {
    if (!teamId) return;
    let cancelled = false;
    (async () => {
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const day = start.getDay() || 7;
      start.setDate(start.getDate() - (day - 1));
      const end = new Date(start);
      end.setDate(end.getDate() + 7);

      const { data: weekSessions } = await supabase
        .from('sessions')
        .select('id, duration_mins')
        .eq('team_id', teamId)
        .gte('date', start.toISOString())
        .lt('date', end.toISOString());

      if (cancelled) return;
      const sessionIds = (weekSessions ?? []).map((s) => s.id as string);

      let drillMinutes = 0;
      if (sessionIds.length) {
        const { data: drills } = await supabase
          .from('session_drills')
          .select('drills(duration_mins)')
          .in('session_id', sessionIds);
        drillMinutes = ((drills ?? []) as SessionDrillDurationRow[])
          .reduce((sum, row) => sum + durationFromSessionDrill(row), 0);
      }

      // Roster count comes from team_players (active rows only). There is
      // no standalone `players` table on the live DB — see migration 00008
      // (team_players is the canonical squad join).
      const { count: total } = await supabase
        .from('team_players')
        .select('id', { count: 'exact', head: true })
        .eq('team_id', teamId)
        .eq('is_active', true);

      if (cancelled) return;
      setMetrics({
        sessionsThisWeek: weekSessions?.length ?? 0,
        drillMinutesPlanned: drillMinutes,
        squadAvailable: total ?? 0,
        squadTotal: total ?? 0,
        squadLimited: 0,
      });
    })();
    return () => { cancelled = true; };
  }, [teamId]);

  return metrics;
}
